import React, { memo } from "react";
import { ActivityIndicator, View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

export interface LoadingStateProps {
  /**
   * Visual presentation:
   * - `"default"` — plain background, renders inline with the page.
   * - `"overlay"` — semi-transparent dark backdrop layered on top of content
   *   via absolute positioning.
   */
  variant?: "default" | "overlay";
  /**
   * Layout sizing:
   * - `"fullscreen"` — `flex-1` so it expands to fill the parent container.
   * - `"default"` — fits its natural container without forcing growth.
   */
  mode?: "fullscreen" | "default";
  /**
   * Optional label rendered below the spinner (translation key).
   * Defaults to `common.loading` — "Loading…". Pass `null` to hide it.
   */
  label?: TranslationKeys | null;
  /** Spinner color. Defaults to the brand primary `#E5265B`. */
  color?: string;
  /** Forwarded to the root View for testing. */
  testID?: string;
  className?: string;
}

/**
 * Reusable loading indicator with flexible layout and overlay support.
 *
 * @example
 * // Default inline loading state
 * <LoadingState />
 *
 * @example
 * // Full-screen loading
 * <LoadingState mode="fullscreen" />
 *
 * @example
 * // Overlay on top of existing content
 * <View style={{ position: "relative" }}>
 *   <SomeContent />
 *   {isLoading && <LoadingState variant="overlay" mode="fullscreen" />}
 * </View>
 *
 * @example
 * // Custom label
 * <LoadingState label="invite.landing.verifying" />
 *
 * @example
 * // No label
 * <LoadingState label={null} />
 */
export const LoadingState = memo(function LoadingState({
  variant = "default",
  mode = "default",
  label = "common.loading",
  color = "#E5265B",
  testID,
  className,
}: LoadingStateProps) {
  const { t } = useI18n();

  const isOverlay = variant === "overlay";
  const isFullscreen = mode === "fullscreen";

  return (
    <View
      testID={testID}
      className={cn(
        "items-center justify-center",
        isFullscreen ? "flex-1" : "py-8",
        isOverlay ? "absolute inset-0 bg-black/40 z-50" : "bg-transparent",
        className
      )}
      accessibilityRole="progressbar"
      accessibilityLabel={label ? t(label) : undefined}
    >
      <ActivityIndicator size="large" color={color} />
      {label != null && (
        <AppText className="text-sm text-dark-text-15 mt-3 text-center">{t(label)}</AppText>
      )}
    </View>
  );
});
